import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import type { ManagedUser, UserCreateRequest, UserUpdateRequest } from "./types";

const USERS_KEY = ["users"] as const;

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({ queryKey: USERS_KEY });
}

export function useUsers() {
  return useQuery({
    queryKey: USERS_KEY,
    queryFn: async () => {
      const body = await apiFetch<{ users: ManagedUser[] }>("/api/v1/users");
      return body.users;
    },
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UserCreateRequest) =>
      apiFetch<ManagedUser>("/api/v1/users", { method: "POST", body }),
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ username, body }: { username: string; body: UserUpdateRequest }) =>
      apiFetch<ManagedUser>(`/api/v1/users/${username}`, { method: "PUT", body }),
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (username: string) => apiFetch<void>(`/api/v1/users/${username}`, { method: "DELETE" }),
    onSuccess: () => invalidate(qc),
  });
}
